import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { books, getCollagesForBook } from '../../data';
import * as d3 from 'd3';
import './BooksListPage.css';
import BooksTimeline from './components/BooksTimeline';
import { IconContext } from 'react-icons';
import BookCatalogue from './components/BookCatalogue';
import { getImagePaths } from '../../utils/imageUtils';
import { drawNoisyImage, drawDefaultPattern } from '../../utils/noise';
import { MdAutoStories, MdAccountTree, MdTimeline } from 'react-icons/md';
import useMediaQuery from '../../hooks/useMediaQuery';
import useDocumentMeta from '../../hooks/useDocumentMeta';

interface NodeDatum extends d3.SimulationNodeDatum {
    id: string;
    title: string;
    coverImage: string;
    author: string;
}

interface LinkDatum extends d3.SimulationLinkDatum<NodeDatum> {
    source: NodeDatum;
    target: NodeDatum;
    strength?: number;
}

type ViewMode = 'shelf' | 'network' | 'timeline';

const ShelfIcon = MdAutoStories as React.ElementType;
const NetworkIcon = MdAccountTree as React.ElementType;
const TimelineIcon = MdTimeline as React.ElementType;

const VIEWS: { mode: ViewMode; label: string; Icon: React.ElementType }[] = [
    /* Was "grid", which is no longer what it is. */
    { mode: 'shelf', label: 'shelf', Icon: ShelfIcon },
    { mode: 'network', label: 'network', Icon: NetworkIcon },
    { mode: 'timeline', label: 'timeline', Icon: TimelineIcon }
];

const BooksListPage: React.FC = () => {
    const [viewMode, setViewMode] = useState<ViewMode>('shelf');
    const networkRef = useRef<SVGSVGElement | null>(null);
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 768px)');

    useDocumentMeta({
        title: 'Library',
        description:
            'The second-hand books, photobooks and stray paper the collages are cut from — as a grid, a network of shared appearances, or a timeline.'
    });

    useEffect(() => {
        if (viewMode !== 'network') return;
        return createNetworkVisualization();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, isMobile]);

    const createNetworkVisualization = () => {
        if (!networkRef.current) return;

        const width = networkRef.current.clientWidth || 1200;
        // The graph is now usable at any width rather than being removed
        // below 768px. Nodes shrink so the layout still breathes.
        const nodeSize = isMobile ? 56 : 100;
        const height = isMobile ? 460 : 600;

        const svg = d3
            .select(networkRef.current)
            .attr('width', width)
            .attr('height', height);

        svg.selectAll('*').remove();

        const defs = svg.append('defs');
        const container = svg.append('g').attr('transform', `translate(${width / 2},${height / 2})`);

        const zoom = d3
            .zoom<SVGSVGElement, unknown>()
            .scaleExtent([0.2, 3])
            .on('zoom', event => container.attr('transform', event.transform))
            .filter(event => {
                if (event.type === 'wheel') return !event.ctrlKey;
                return !event.button && event.type !== 'dblclick';
            });

        svg.call(zoom).on('dblclick.zoom', null);

        const nodes: NodeDatum[] = books.map(book => ({
            id: book.id,
            title: book.title,
            coverImage: book.coverImage,
            author: book.author
        }));

        const links: LinkDatum[] = [];
        books.forEach((book1, i) => {
            books.forEach((book2, j) => {
                if (i >= j) return;
                const a = getCollagesForBook(book1.id);
                const b = getCollagesForBook(book2.id);
                const shared = a.filter(collage => b.some(c => c.id === collage.id));
                if (shared.length > 0) {
                    links.push({ source: nodes[i], target: nodes[j], strength: shared.length });
                }
            });
        });

        // Each cover is rendered noisy once, into a pattern. The previous
        // version re-ran the whole noise pass *and* canvas.toDataURL() on
        // every animation frame of every hovered node — a full PNG encode
        // ten times a second. Hover is now a CSS highlight instead.
        nodes.forEach(node => {
            const pattern = defs
                .append('pattern')
                .attr('id', `cover-${node.id}`)
                .attr('width', 1)
                .attr('height', 1);

            const canvas = document.createElement('canvas');
            canvas.width = nodeSize;
            canvas.height = nodeSize;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;

            const image = pattern
                .append('image')
                .attr('width', nodeSize)
                .attr('height', nodeSize)
                .attr('preserveAspectRatio', 'xMidYMid slice');

            const img = new Image();
            const { thumbnailWebp, thumbnail } = getImagePaths(node.coverImage || '');
            img.onload = () => {
                drawNoisyImage(ctx, img, nodeSize);
                image.attr('href', canvas.toDataURL());
            };
            img.onerror = () => {
                drawDefaultPattern(ctx, nodeSize, nodeSize);
                image.attr('href', canvas.toDataURL());
            };
            img.src = thumbnailWebp || thumbnail;
        });

        const linksGroup = container.append('g').attr('class', 'links-group');
        const nodesGroup = container.append('g').attr('class', 'nodes-group');

        const link = linksGroup
            .selectAll('line')
            .data(links)
            .join('line')
            .attr('class', 'books-network-link')
            .attr('stroke-width', d => Math.sqrt((d.strength || 1) * 2));

        const go = (d: NodeDatum) => navigate(`/books/${d.id}`);

        const node = nodesGroup
            .selectAll<SVGGElement, NodeDatum>('g')
            .data(nodes)
            .join('g')
            .attr('class', 'books-network-node')
            .attr('tabindex', 0)
            .attr('role', 'link')
            .attr('aria-label', d => `${d.title} by ${d.author}`)
            .style('cursor', 'pointer')
            .on('click', (event, d) => {
                if (event.defaultPrevented) return;
                go(d);
            })
            .on('keydown', (event: KeyboardEvent, d) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    go(d);
                }
            })
            .call(
                d3
                    .drag<SVGGElement, NodeDatum>()
                    .on('start', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0.1).restart();
                        d.fx = d.x;
                        d.fy = d.y;
                    })
                    .on('drag', (event, d) => {
                        d.fx = event.x;
                        d.fy = event.y;
                    })
                    .on('end', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0);
                        d.fx = null;
                        d.fy = null;
                    })
            );

        node.append('rect')
            .attr('class', 'node-backdrop')
            .attr('width', nodeSize)
            .attr('height', nodeSize)
            .attr('x', -nodeSize / 2)
            .attr('y', -nodeSize / 2);

        node.append('rect')
            .attr('class', 'node-cover')
            .attr('width', nodeSize)
            .attr('height', nodeSize)
            .attr('x', -nodeSize / 2)
            .attr('y', -nodeSize / 2)
            .attr('fill', d => `url(#cover-${d.id})`);

        const simulation = d3
            .forceSimulation<NodeDatum>(nodes)
            .force(
                'link',
                d3
                    .forceLink<NodeDatum, LinkDatum>(links)
                    .id(d => d.id)
                    .distance(isMobile ? 120 : 200)
                    .strength(d => (d.strength || 1) * 0.1)
            )
            .force('charge', d3.forceManyBody().strength(isMobile ? -150 : -300))
            .force('collide', d3.forceCollide().radius(nodeSize * 0.8).strength(0.3))
            .force('x', d3.forceX().strength(0.02))
            .force('y', d3.forceY().strength(0.02))
            .velocityDecay(0.6)
            .on('tick', () => {
                link
                    .attr('x1', d => d.source.x ?? 0)
                    .attr('y1', d => d.source.y ?? 0)
                    .attr('x2', d => d.target.x ?? 0)
                    .attr('y2', d => d.target.y ?? 0);
                node.attr('transform', d => `translate(${d.x ?? 0},${d.y ?? 0})`);
            });

        // The old cleanup only removed zoom listeners and left the
        // simulation running forever after switching views.
        return () => {
            simulation.stop();
            svg.on('.zoom', null);
        };
    };

    return (
        <IconContext.Provider value={{ className: 'view-icon' }}>
            <div className="books-container">
                <div className="books-main-content">
                    <div className="books-header">
                        <div className="title-section">
                            <h1 className="books-title">Library</h1>
                            <p className="books-description">
                                The books, photobooks and stray paper the collages are cut
                                from. Photographs don't do most of them justice.
                            </p>
                        </div>

                        {/* The three views used to be hidden inside a
                            sentence as italic 0.9em spans with onClick — not
                            focusable, and easy to read straight past. They
                            are now labelled buttons in a visible group. */}
                        <div
                            className="view-switcher"
                            role="group"
                            aria-label="Choose how to browse the library"
                        >
                            {VIEWS.map(({ mode, label, Icon }) => (
                                <button
                                    key={mode}
                                    type="button"
                                    className={`view-option ${viewMode === mode ? 'active' : ''}`}
                                    onClick={() => setViewMode(mode)}
                                    aria-pressed={viewMode === mode}
                                    /* Below 380px .view-label is hidden and the
                                       button is icon-only, so the name has to
                                       come from somewhere. */
                                    aria-label={label}
                                >
                                    <Icon />
                                    <span className="view-label">{label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {viewMode === 'shelf' && <BookCatalogue books={books} />}

                    {viewMode === 'network' && (
                        <>
                            <p className="view-note">
                                Books linked when they appear in the same collage. Drag to
                                rearrange, scroll to zoom.
                            </p>
                            <svg
                                ref={networkRef}
                                className="books-network"
                                role="group"
                                aria-label="Books linked by shared collages"
                            />
                        </>
                    )}

                    {viewMode === 'timeline' && <BooksTimeline books={books} />}
                </div>
            </div>
        </IconContext.Provider>
    );
};

export default BooksListPage;
